// ===============================
// 🔧 CRUSHER UI KIT CORE IMPORTS
// ===============================

// --- Generated by Style Dictionary ---
import '../css/tokens.css';
import '../css/modes.css';
import '../css/semantic.css';
import '../css/bridge.css';
import '../css/code-theme.css';
import '../css/theme-scenes.css';
import '../css/scenes.css';
import '../css/dialect-overrides.css';

// Per-theme token overlays (generated)
import '../css/themes/glass.css';
import '../css/themes/brutal.css';
import '../css/themes/neumorph.css';
import '../css/themes/neobrutal.css';
import '../css/themes/minimal.css';
import '../css/themes/futuristic.css';
import '../css/themes/bento.css';

// --- Base SCSS (Utilities, Reset, Typography) ---
import '../scss/main.scss';

// ===============================
// 🧩 COMPONENT REGISTRATION
// ===============================

// --- Atoms ---
import '../components/atoms/crusher-button.js';
import '../components/atoms/crusher-icon-link.js';
import '../components/atoms/crusher-input.js';
import '../components/atoms/crusher-textarea.js';
import '../components/atoms/crusher-checkbox.js';
import '../components/atoms/crusher-switch.js';
import '../components/atoms/crusher-chip.js';
import '../components/atoms/crusher-code-block.js';
import '../components/atoms/crusher-tooltip.js';
import '../components/atoms/crusher-badge.js';
import '../components/atoms/crusher-currency-input.js';

// --- Molecules ---
import '../components/molecules/crusher-card.js';
import '../components/molecules/crusher-card-link.js';
import '../components/molecules/crusher-grid.js';
import '../components/molecules/crusher-modal.js';
import '../components/molecules/crusher-skill-bar.js';
import '../components/molecules/crusher-stack.js';
import '../components/molecules/crusher-style-switcher.js';
import '../components/molecules/crusher-audio-player.js';
import '../components/molecules/crusher-toast-center.js';
import '../components/molecules/crusher-dropdown.js';
import '../components/molecules/crusher-empty-state.js';
import '../components/molecules/crusher-filter-bar.js';
import '../components/molecules/crusher-tabs.js';
import '../components/molecules/crusher-command-palette.js';
import '../components/molecules/crusher-select.js';
import '../components/molecules/crusher-menubar.js';
import '../components/molecules/crusher-stat.js';
import '../components/molecules/crusher-toolbar.js';
import '../components/molecules/crusher-alert.js';
import '../components/molecules/crusher-banner.js';
import '../components/molecules/crusher-disclosure.js';
import '../components/molecules/crusher-connection-status.js';
import '../components/molecules/crusher-avatar-menu.js';
import '../components/molecules/crusher-plan-card.js';
import '../components/molecules/crusher-invoice-list.js';
import '../components/molecules/crusher-payment-method-card.js';
import '../components/molecules/crusher-file-upload.js';
import '../components/molecules/crusher-sparkline.js';
import '../components/molecules/crusher-chart.js';
import '../components/molecules/crusher-kpi.js';

// --- Organisms ---
import '../components/organisms/crusher-app-shell.js';
import '../components/organisms/crusher-nav-list.js';
import '../components/organisms/crusher-page-header.js';
import '../components/organisms/crusher-section-title.js';
import '../components/organisms/crusher-timeline-item.js';
import '../components/organisms/crusher-timeline.js';
import '../components/organisms/crusher-table.js';

// --- Form primitives ---
import '../components/forms/crusher-field.js';
import '../components/forms/crusher-label.js';
import '../components/forms/crusher-hint.js';
import '../components/forms/crusher-error.js';

// --- Runtime (Theme + Mode Switching) ---
import '../runtime/theme.js';
import '../runtime/toast.js'; // exposes window.crusherToast(...)
import '../runtime/command-palette.js';
import { setScene, setSceneInputs, listScenes } from '../runtime/scenes.js';

// ===============================
// Environment Scene Studio
// ===============================
function initSceneStudio() {
  const chips = document.getElementById('scene-chips');
  const stageName = document.getElementById('scene-stage-name');
  const stageMood = document.getElementById('scene-stage-mood');
  const stageStat = document.getElementById('scene-stage-stat');
  const intensity = document.getElementById('scene-intensity');
  const clear = document.getElementById('scene-clear');
  if (!chips) return;

  const scenes = listScenes();
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  for (const sc of scenes) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'scene-chip';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', 'false');
    btn.textContent = cap(sc.name);
    btn.addEventListener('click', () => {
      setScene(sc.name, { intensity: Number(intensity.value) || 1 });
      chips.querySelectorAll('.scene-chip').forEach((c) => c.setAttribute('aria-selected', 'false'));
      btn.setAttribute('aria-selected', 'true');
      stageName.textContent = cap(sc.name);
      stageMood.textContent = sc.mood;
      if (stageStat) stageStat.setAttribute('value', sc.name);
    });
    chips.appendChild(btn);
  }
  intensity.addEventListener('input', () => setSceneInputs({ intensity: Number(intensity.value) }));
  clear.addEventListener('click', () => {
    setScene('');
    chips.querySelectorAll('.scene-chip').forEach((c) => c.setAttribute('aria-selected', 'false'));
    stageName.textContent = 'None';
    stageMood.textContent = 'A clean canvas, no atmosphere';
    if (stageStat) stageStat.setAttribute('value', 'none');
  });

  // Default the stage to sunny so the section opens alive.
  const sunny = chips.querySelector('.scene-chip');
  if (sunny) sunny.click();
}

// ===============================
// Theme Studio: live radius + brand + dialect/mode, share URL
// ===============================
function initThemeStudio() {
  const radius = document.getElementById('studio-radius');
  const radiusVal = document.getElementById('studio-radius-val');
  const brand = document.getElementById('studio-brand');
  const theme = document.getElementById('studio-theme');
  const mode = document.getElementById('studio-mode');
  const share = document.getElementById('studio-share');
  const reset = document.getElementById('studio-reset');
  const shareOut = document.getElementById('studio-share-out');
  if (!radius) return;

  const RADIUS_BASE = { sm: 4, md: 8, lg: 16, xl: 20 };
  const root = document.documentElement;

  function applyRadius(m) {
    for (const [k, v] of Object.entries(RADIUS_BASE)) {
      root.style.setProperty(`--crusher-radius-${k}`, `${(v * m).toFixed(1)}px`);
    }
    if (radiusVal) radiusVal.textContent = `${m.toFixed(1)}x`;
  }

  function readState() {
    return {
      r: radius.value,
      b: brand.value,
      t: theme.value || theme.querySelector('option[selected]')?.value || 'glass',
      m: mode.value || mode.querySelector('option[selected]')?.value || 'light',
    };
  }

  function applyFromHash() {
    const h = new URLSearchParams(window.location.hash.replace(/^#studio\?/, ''));
    if (h.has('r')) { radius.value = h.get('r'); applyRadius(Number(h.get('r'))); }
    if (h.has('b')) { brand.value = h.get('b'); root.style.setProperty('--crusher-color-brand-primary', h.get('b')); }
    if (h.has('t')) { theme.value = h.get('t'); root.setAttribute('data-theme', h.get('t')); }
    if (h.has('m')) { mode.value = h.get('m'); root.setAttribute('data-mode', h.get('m')); }
  }

  radius.addEventListener('input', () => applyRadius(Number(radius.value)));
  brand.addEventListener('input', () => root.style.setProperty('--crusher-color-brand-primary', brand.value));
  theme.addEventListener('change', () => root.setAttribute('data-theme', theme.value));
  mode.addEventListener('change', () => root.setAttribute('data-mode', mode.value));

  share.addEventListener('click', () => {
    const s = readState();
    const url = `${window.location.origin}${window.location.pathname}#studio?r=${s.r}&b=${encodeURIComponent(s.b)}&t=${s.t}&m=${s.m}`;
    navigator.clipboard?.writeText(url).then(
      () => { shareOut.textContent = 'Copied: ' + url; },
      () => { shareOut.textContent = url; },
    );
  });

  reset.addEventListener('click', () => {
    radius.value = 1; applyRadius(1);
    brand.value = '#22c55e'; root.style.setProperty('--crusher-color-brand-primary', '#22c55e');
    theme.value = 'glass'; root.setAttribute('data-theme', 'glass');
    mode.value = 'light'; root.setAttribute('data-mode', 'light');
    shareOut.textContent = '';
    history.replaceState(null, '', window.location.pathname);
  });

  applyFromHash();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { initSceneStudio(); initThemeStudio(); });
} else {
  initSceneStudio();
  initThemeStudio();
}

// ===============================
// 🚀 READY
// ===============================
console.log('%c Crusher UI Kit Loaded ', 'background:#22c55e;color:#fff;padding:4px 8px;border-radius:4px;');
